"use client";

import { useCallback, useEffect, useState, useRef, useMemo, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import ImageActionToolbar, { ActionIconButton } from "./ImageActionToolbar";
import ImageEditActionIcon from "./ImageEditActionIcon";
import ImageLayerRecognitionModal from "./ImageLayerRecognitionModal";
import type { ImageLayerTask } from "../../lib/image-layer-api";
import { NextStepTarget, patchTaskOutputImage } from "../../lib/ecom-image-api";
import { downloadFile, downloadFiles } from "../../lib/download";
import { preloadGeneratedImages } from "../../lib/image-utils";
import type { EcomActiveRefineState, EcomRefineTrackInfo } from "../../lib/ecom-tab-history";
import dynamic from "next/dynamic";
import RefineOverlay from "./RefineOverlay";
import { useRefineLifecycle } from "./useRefineLifecycle";
import ImageEditModal, { ImageEditSaveResult } from "../image-edit/ImageEditModal";
import { resolveMediaUrl } from "../../lib/resolve-media-url";
import { preloadRefineImage } from "../../lib/ecom-refine-image-loading";
import { replaceEditedImages } from "../../lib/image-edit-save";
import SafeImage from "./SafeImage";
import {
  createToneAdjustedBlob,
  loadToneRecords,
  saveToneRecords,
  type ToneRecord,
} from "../../lib/image-tone";
import { imageEditApi } from "../../lib/image-edit-api";
import { getSafeErrorMessage } from "../../lib/error-codes";
import { Loader2 } from "lucide-react";
import GeneratingCard from "./EcomGeneratingCard";
import { useLatestRefs } from "../../hooks/useLatestRefs";

const ImageRefineEditor = dynamic(
  () => import("./ImageRefineEditor"),
  { ssr: false }
);

let refineEditorResourcesPromise: Promise<void> | null = null;

function preloadRefineEditorResources(): Promise<void> {
  if (!refineEditorResourcesPromise) {
    refineEditorResourcesPromise = Promise.all([
      import("./ImageRefineEditor"),
      import("fabric"),
    ]).then(() => undefined).catch(() => {
      refineEditorResourcesPromise = null;
    });
  }
  return refineEditorResourcesPromise;
}

// Use resolveMediaUrl as the single gate for URL validity so that malformed
// public storage placeholders (e.g. `/api/v1/public/storage/file` with no uuid)
// are rejected and never rendered into <img src> / preload.
function isMediaUrl(s: string | null | undefined): boolean {
  const resolved = resolveMediaUrl(s);
  if (!resolved) return false;
  return (
    resolved.startsWith("http://") ||
    resolved.startsWith("https://") ||
    resolved.startsWith("/api/") ||
    resolved.startsWith("data:") ||
    resolved.startsWith("blob:")
  );
}

function isVideoUrl(s: string): boolean {
  if (!isMediaUrl(s)) return false;
  return /\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(s);
}

function toRefineEditableUrl(raw: string): string {
  // Pass the direct (CDN) URL through — FabricCanvas builds its own candidate
  // list with the server proxy as fallback. Pre-wrapping into the proxy here
  // would force every editor open through the bandwidth-limited origin and
  // skip the browser cache entry already created by the preview grid.
  return resolveMediaUrl(raw);
}

interface PreviewAreaProps {
  /** Conversation delivery reuses these image tools without rewriting its source. */
  delivery?: { index: number; total: number; actions: readonly string[];
    toolbar?: { target: HTMLDivElement | null; current: boolean; transitioning: boolean };
    /** `tile` / `stack` follow the image's natural height. */
    layout?: 'tile' | 'stack';
  };
  status: "empty" | "generating" | "completed" | "failed";
  progress?: number;
  generationStartedAt?: number | null;
  imageUrl?: string;
  modelImage?: string | null;
  productImage?: string | null;
  backgroundImage?: string | null;
  subOption?: string;
  onDownload?: () => void;
  onRegenerate?: () => void;
  onFavorite?: (imageUrl: string) => void;
  onPublish?: (imageUrl: string) => void;
  onPreview?: (imageUrl: string, index?: number) => void;
  nextStepTargets?: NextStepTarget[];
  onNextStep?: (target: NextStepTarget, selectedImages: string[]) => void;
  onRegenerateSingle?: (index: number) => void;
  regeneratingIndexes?: number[];
  failedIndexes?: number[];
  productFrontImage?: string | null;
  productBackImage?: string | null;
  generatedImages?: string[];
  outputCount?: number;
  errorMessage?: string;
  taskProgresses?: number[];
  aspectRatio?: string | null;
  outputCropSize?: string | null;
  /** 每槽位（batch_index-1）独立比例（若设置）；不设置时回退到 aspectRatio。 */
  aspectRatios?: (string | null)[];
  /** 每槽位独立 cropSize（若设置）；不设置时回退到 outputCropSize。 */
  outputCropSizes?: (string | null)[];
  statusHint?: string;
  uploadHoverPreview?: { url: string; alt?: string } | null;
  onMakeSame?: (workflowKey: string, prompt: string) => void;
  taskId?: string;
  batchChildTaskIds?: string[];
  onImagesUpdated?: (images: string[], targetTaskId?: string) => void;
  onHistoryRefresh?: () => void;
  onAutoStitch?: () => void;
  onRefiningChange?: (active: boolean) => void;
  refineOwnerTabId: string;
  onRefineTrack?: (info: EcomRefineTrackInfo) => boolean | void;
  /** Conversation delivery: show the saved edit in place; false when the slot is busy. */
  onDeliveryImageReplace?: (editedUrl: string) => boolean;
  activeRefines?: Record<number, EcomActiveRefineState>;
  refineStateVersion?: number;
  onBatchDownload?: (urls: string[]) => void;
  historyImageLayer?: { task: ImageLayerTask; imageUrl: string } | null;
}

function parseAspectRatio(ratio: string | null | undefined): { w: number; h: number } {
  if (!ratio || ratio === "auto") return { w: 3, h: 4 };
  const parts = ratio.split(":");
  if (parts.length === 2) {
    const w = parseInt(parts[0], 10);
    const h = parseInt(parts[1], 10);
    if (w > 0 && h > 0) return { w, h };
  }
  return { w: 3, h: 4 };
}

function parseOutputCropSize(size: string | null | undefined): { w: number; h: number } | null {
  if (!size) return null;
  const match = size.trim().match(/^(\d+)\s*[x*]\s*(\d+)$/i);
  if (!match) return null;
  const w = parseInt(match[1], 10);
  const h = parseInt(match[2], 10);
  return w > 0 && h > 0 ? { w, h } : null;
}

function failSlotSize(outputCount: number): "large" | "small" | "compact" {
  if (outputCount <= 1) return "large";
  if (outputCount <= 6) return "small";
  return "compact";
}

function FailedCard({
  errorMessage,
  onRetry,
  size = "large",
}: {
  errorMessage?: string;
  onRetry?: () => void;
  size?: "large" | "small" | "compact";
}) {
  const isLarge = size === "large";
  const isCompact = size === "compact";
  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center ${isCompact ? "px-0.5 py-0.5" : ""}`}
      style={{
        borderRadius: "9.23px",
        background: "linear-gradient(135deg, #2D1B2E 0%, #1B1025 50%, #1A0A1E 100%)",
        animation: "ecomFadeInUp 0.4s ease-out",
      }}
    >
      <div
        className={`rounded-full bg-red-500/10 flex items-center justify-center shrink-0 ${
          isLarge ? "w-16 h-16 mb-3" : isCompact ? "w-7 h-7 mb-1" : "w-10 h-10 mb-2"
        }`}
      >
        <svg
          className={`text-red-400 ${isLarge ? "w-8 h-8" : isCompact ? "w-4 h-4" : "w-5 h-5"}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
        </svg>
      </div>
      <span
        className={`text-red-400 font-medium text-center leading-tight ${isLarge ? "text-sm" : isCompact ? "text-[10px]" : "text-xs"}`}
      >
        生成失败
      </span>
      {isLarge && errorMessage && (
        <span className="text-xs text-white/40 mt-1 mb-2 max-w-[80%] text-center line-clamp-2">{errorMessage}</span>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={
            isLarge
              ? "mt-3 px-5 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-xs text-white/70 hover:text-white transition-all border border-white/10"
              : isCompact
                ? "mt-1 px-2 py-0.5 rounded-md bg-white/10 hover:bg-white/15 text-[10px] text-white/70 hover:text-white transition-all border border-white/10"
                : "mt-2 px-3 py-1 rounded-md bg-white/10 hover:bg-white/15 text-[11px] text-white/70 hover:text-white transition-all border border-white/10"
          }
        >
          {isLarge ? "重新生成" : "重试"}
        </button>
      )}
    </div>
  );
}

export default function PreviewArea({
  delivery,
  status,
  progress = 0,
  generationStartedAt,
  imageUrl: rawImageUrlProp,
  modelImage: modelImageProp,
  productImage: productImageProp,
  backgroundImage: backgroundImageProp,
  subOption,
  onRegenerate,
  onFavorite,
  onPublish,
  onPreview,
  nextStepTargets,
  onNextStep,
  productFrontImage: productFrontImageProp,
  productBackImage: productBackImageProp,
  generatedImages: rawGeneratedImagesProp = [],
  outputCount: rawOutputCount = 4,
  errorMessage,
  onRegenerateSingle,
  regeneratingIndexes = [],
  failedIndexes = [],
  taskProgresses = [],
  aspectRatio,
  outputCropSize,
  aspectRatios,
  outputCropSizes,
  statusHint,
  uploadHoverPreview,
  taskId = "",
  batchChildTaskIds = [],
  onImagesUpdated,
  onHistoryRefresh,
  onAutoStitch,
  onRefiningChange,
  refineOwnerTabId,
  onRefineTrack,
  onDeliveryImageReplace,
  activeRefines,
  refineStateVersion,
  onBatchDownload,
  historyImageLayer,
}: PreviewAreaProps) {
  // Normalize all inbound media URLs through resolveMediaUrl so that
  // - old gateway absolute URLs become same-origin /api/v1/... paths
  // - malformed public-storage placeholders collapse to '' and never reach <img>
  const rawImageUrl = useMemo(() => resolveMediaUrl(rawImageUrlProp), [rawImageUrlProp]);
  const rawGeneratedImages = useMemo(
    () => rawGeneratedImagesProp.map((img) => resolveMediaUrl(img)),
    [rawGeneratedImagesProp]
  );
  const modelImage = useMemo(() => resolveMediaUrl(modelImageProp), [modelImageProp]);
  const productImage = useMemo(() => resolveMediaUrl(productImageProp), [productImageProp]);
  const backgroundImage = useMemo(() => resolveMediaUrl(backgroundImageProp), [backgroundImageProp]);
  const productFrontImage = useMemo(
    () => resolveMediaUrl(productFrontImageProp),
    [productFrontImageProp]
  );
  const productBackImage = useMemo(
    () => resolveMediaUrl(productBackImageProp),
    [productBackImageProp]
  );
  const hoverPreviewUrl = useMemo(
    () => resolveMediaUrl(uploadHoverPreview?.url || ""),
    [uploadHoverPreview?.url],
  );

  const expectedRatio = parseOutputCropSize(outputCropSize) ?? parseAspectRatio(aspectRatio);
  const [detectedRatio, setDetectedRatio] = useState<{ w: number; h: number } | null>(null);
  const [imageDimensions, setImageDimensions] = useState<Record<number, { w: number; h: number }>>({});

  // Refine editor state
  const [refineEditorOpen, setRefineEditorOpen] = useState(false);
  const [refineImageUrl, setRefineImageUrl] = useState("");
  const [refineImageIndex, setRefineImageIndex] = useState(0);
  const [recognitionImageUrl, setRecognitionImageUrl] = useState("");
  const [recognitionTask, setRecognitionTask] = useState<ImageLayerTask | null>(null);
  const [imageEditOpen, setImageEditOpen] = useState(false);
  const [imageEditMode, setImageEditMode] = useState<"single" | "batch">("single");
  const [imageEditInitialId, setImageEditInitialId] = useState("");
  const [refineFrozenImages, setRefineFrozenImages] = useState<Record<number, string>>({});

  const {
    onImagesUpdated: onImagesUpdatedRef,
    taskId: taskIdRef,
    batchChildTaskIds: batchChildTaskIdsRef,
    onHistoryRefresh: onHistoryRefreshRef,
  } = useLatestRefs({ onImagesUpdated, taskId, batchChildTaskIds, onHistoryRefresh });

  const { refiningIndexes, handleRefineStart } = useRefineLifecycle({
    ownerTabId: refineOwnerTabId,
    imageIndex: refineImageIndex,
    taskId,
    batchChildTaskIds,
    activeRefines,
    sourceImages: rawGeneratedImages.length > 0 ? rawGeneratedImages : rawImageUrl ? [rawImageUrl] : [],
    lifecycleVersion: refineStateVersion,
    onTrack: onRefineTrack,
    onHistoryRefresh,
    onRefiningChange,
    onFailure: (message) => setToastMessage({ text: message, type: "error" }),
  });

  useEffect(() => {
    const activeIndexes = new Set<number>();
    if (activeRefines) {
      for (const idx of Object.keys(activeRefines)) activeIndexes.add(Number(idx));
    }
    for (const idx of Object.keys(refiningIndexes)) activeIndexes.add(Number(idx));

    setRefineFrozenImages(prev => {
      const next: Record<number, string> = {};
      for (const idx of Array.from(activeIndexes).sort((a, b) => a - b)) {
        const frozen = prev[idx] || rawGeneratedImages[idx] || (idx === 0 ? rawImageUrl : "");
        if (frozen) next[idx] = frozen;
      }
      const prevKeys = Object.keys(prev).sort().join(",");
      const nextKeys = Object.keys(next).sort().join(",");
      if (prevKeys !== nextKeys) return next;
      return Object.keys(next).some(k => prev[Number(k)] !== next[Number(k)]) ? next : prev;
    });
  }, [activeRefines, refiningIndexes, rawGeneratedImages, rawImageUrl]);

  const imageUrl = refineFrozenImages[0] || rawImageUrl;
  const generatedImages = useMemo(
    () => rawGeneratedImages.map((img, idx) => refineFrozenImages[idx] || img),
    [rawGeneratedImages, refineFrozenImages]
  );
  const outputCount = Math.max(rawOutputCount, generatedImages.filter(Boolean).length || 0);

  useEffect(() => {
    const hasEditableImage = [imageUrl, ...generatedImages]
      .some((img) => isMediaUrl(img) && !isVideoUrl(img));
    if (hasEditableImage) void preloadRefineEditorResources();
  }, [generatedImages, imageUrl]);

  const preloadRefineEditor = useCallback((imgUrl: string) => {
    const editableUrl = toRefineEditableUrl(imgUrl);
    if (!editableUrl) return "";
    void preloadRefineEditorResources();
    preloadRefineImage(editableUrl);
    return editableUrl;
  }, []);

  // Open refine editor for a specific image
  const handleOpenRefine = useCallback((imgUrl: string, imgIndex: number) => {
    const editableUrl = preloadRefineEditor(imgUrl);
    if (!editableUrl) {
      setToastMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      return;
    }
    setRefineImageUrl(editableUrl);
    setRefineImageIndex(imgIndex);
    setRefineEditorOpen(true);
  }, [preloadRefineEditor]);

  const [toastMessage, setToastMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  useEffect(() => {
    setDetectedRatio(null);
    setImageDimensions({});
  }, [generatedImages]);

  // 生成完成后预加载图片到浏览器缓存
  useEffect(() => {
    if (status === "completed" && generatedImages.length > 0) {
      const validUrls = generatedImages.filter(Boolean);
      if (validUrls.length > 0) {
        preloadGeneratedImages(validUrls);
      }
    }
  }, [status, generatedImages]);

  const handleBatchImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>, idx: number) => {
    const img = e.currentTarget;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setDetectedRatio(prev => prev ?? { w: img.naturalWidth, h: img.naturalHeight });
      setImageDimensions(prev => {
        const next = { w: img.naturalWidth, h: img.naturalHeight };
        const current = prev[idx];
        if (current?.w === next.w && current?.h === next.h) return prev;
        return { ...prev, [idx]: next };
      });
    }
  }, []);

  const effectiveRatio = detectedRatio ?? expectedRatio;
  const aspectInlineStyle = { aspectRatio: `${effectiveRatio.w}/${effectiveRatio.h}` };
  const previewRatio = effectiveRatio.w / effectiveRatio.h;
  const singleGeneratingCardStyle: CSSProperties = {
    ...aspectInlineStyle,
    width: "min(100%, calc((100vh - 240px) * var(--preview-ratio)))",
    maxHeight: "calc(100vh - 240px)",
    ["--preview-ratio" as string]: String(previewRatio),
  };
  const handleSingleDownload = async (url: string) => {
    await downloadFile(url, `flowx_${Date.now()}.png`);
  };

  const handleBatchDownload = () => {
    // Download selected images if any are selected, otherwise download all valid images
    const urls = isMultiOutput
      ? generatedImages.filter((img, idx) => selectedIndexes.has(idx) && img)
      : generatedImages.filter(Boolean);
    if (urls.length > 0 && onBatchDownload) {
      onBatchDownload(urls);
    } else if (urls.length > 0) {
      urls.forEach(u => downloadFile(u, `flowx_${Date.now()}.png`));
    }
  };
  void handleBatchDownload;

  const imageEditSourceImages = useMemo(
    () => generatedImages.length > 0
      ? generatedImages
      : imageUrl ? [imageUrl] : [],
    [generatedImages, imageUrl],
  );
  const imageEditSourceImagesRef = useRef(imageEditSourceImages);
  imageEditSourceImagesRef.current = imageEditSourceImages;
  const imageEditItems = useMemo(
    () => imageEditSourceImages
      .map((url, idx) => ({ id: `image-${idx}`, url, index: idx }))
      .filter((item) => item.url && isMediaUrl(item.url) && !isVideoUrl(item.url)),
    [imageEditSourceImages],
  );

  const handleOpenImageEdit = useCallback((idx: number, mode: "single" | "batch" = "single") => {
    const item = imageEditItems.find((candidate) => candidate.index === idx) || imageEditItems[0];
    if (!item) {
      setToastMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      return;
    }
    setImageEditMode(mode);
    setImageEditInitialId(item.id);
    setImageEditOpen(true);
  }, [imageEditItems]);

  useEffect(() => {
    if (!historyImageLayer?.task) return;
    setRecognitionTask(historyImageLayer.task);
    setRecognitionImageUrl(historyImageLayer.imageUrl || historyImageLayer.task.source_image_url || historyImageLayer.task.image?.url || "");
  }, [historyImageLayer]);

  const handleImageEditSave = useCallback(async (
    results: ImageEditSaveResult[],
    options?: { silent?: boolean },
  ) => {
    if (results.length === 0) return;
    if (delivery) {
      // The delivered asset stays intact; the saved copy only replaces what this slot shows.
      if (!onDeliveryImageReplace?.(results[0].editedUrl)) throw new Error("E8102 图片保存失败，请稍后重试");
      if (!options?.silent) setToastMessage({ text: "调色结果已保存", type: "success" });
      return;
    }
    // Single-output tasks often provide only imageUrl while generatedImages is
    // empty. Use the same source array that opened the editor so an uploaded
    // edit can still resolve index 0 and reach the task PATCH endpoint.
    const resolved = replaceEditedImages(imageEditSourceImagesRef.current, results);
    if (resolved.replacements.length === 0) {
      throw new Error("E8102 图片保存失败，请稍后重试");
    }

    const patchJobs = resolved.replacements.map((replacement) => {
      const targetTaskId = batchChildTaskIdsRef.current?.[replacement.targetIndex] || taskIdRef.current;
      if (!targetTaskId) throw new Error("E8102 图片保存失败，请稍后重试");
      const patchIndex = batchChildTaskIdsRef.current?.[replacement.targetIndex]
        ? 0
        : replacement.targetIndex;
      return patchTaskOutputImage(targetTaskId, patchIndex, replacement.editedUrl);
    });

    await Promise.all(patchJobs);
    onImagesUpdatedRef.current?.(resolved.images, taskIdRef.current);
    onHistoryRefreshRef.current?.();
    if (!options?.silent) {
      setToastMessage({ text: "调色结果已保存", type: "success" });
    }
  }, [delivery, onDeliveryImageReplace]);

  // 快捷调色弹窗：实时预览（idx → 预览图 object URL）
  const [tonePreview, setTonePreview] = useState<{ idx: number; url: string } | null>(null);
  const tonePreviewClearRef = useRef<{ idx: number; previewUrl: string; sourceUrl: string } | null>(null);
  const tonePreviewSourceRef = useRef(generatedImages);
  const tonePreviewBaseSourceRef = useRef<Record<number, string>>({});
  const tonePreviewRef = useRef<{ idx: number; url: string } | null>(null);
  const tonePreviewSwapTimeoutsRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const [toneApplyingIndexes, setToneApplyingIndexes] = useState<Set<number>>(new Set());

  tonePreviewRef.current = tonePreview;

  const finalizeTonePreviewSwap = useCallback((pending: { idx: number; previewUrl: string; sourceUrl: string }) => {
    if (tonePreviewClearRef.current !== pending) return;
    const timeout = tonePreviewSwapTimeoutsRef.current[pending.idx];
    if (timeout) {
      clearTimeout(timeout);
      delete tonePreviewSwapTimeoutsRef.current[pending.idx];
    }
    tonePreviewClearRef.current = null;
    tonePreviewRef.current = null;
    setTonePreview((current) => (
      current?.idx === pending.idx && current.url === pending.previewUrl ? null : current
    ));
    setToneApplyingIndexes((prev) => {
      const next = new Set(prev);
      next.delete(pending.idx);
      return next;
    });
  }, []);

  const tryDecodeTonePreviewSwap = useCallback((pending: { idx: number; previewUrl: string; sourceUrl: string }) => {
    const nextUrl = tonePreviewSourceRef.current[pending.idx];
    // The parent may not have committed the replacement URL yet. The effect
    // watching generatedImages will retry when it does.
    if (!nextUrl || nextUrl === pending.sourceUrl) return;

    const image = new Image();
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      // A failed decode must also release the overlay; otherwise one bad CDN
      // response leaves the result card blocked forever.
      finalizeTonePreviewSwap(pending);
    };
    image.onload = settle;
    image.onerror = settle;
    image.src = nextUrl;
  }, [finalizeTonePreviewSwap]);

  const handleTonePreviewChange = useCallback((idx: number, url: string | null) => {
    if (url) {
      const previousPending = tonePreviewClearRef.current;
      if (previousPending) finalizeTonePreviewSwap(previousPending);
      tonePreviewBaseSourceRef.current[idx] = tonePreviewSourceRef.current[idx] || "";
      tonePreviewRef.current = { idx, url };
      setTonePreview({ idx, url });
      return;
    }
    const current = tonePreviewRef.current;
    if (!current || current.idx !== idx) return;
    // 保存替换完成后先保留预览，等待父层的新图片 URL 到达并完成解码。
    const pending = {
      idx,
      previewUrl: current.url,
      sourceUrl: tonePreviewBaseSourceRef.current[idx] || "",
    };
    tonePreviewClearRef.current = pending;
    // Always provide a bounded fallback. This covers a missing preview swap,
    // an image that never emits load/error, and a parent tab switch during save.
    tonePreviewSwapTimeoutsRef.current[idx] = setTimeout(() => {
      finalizeTonePreviewSwap(pending);
    }, 5000);
    tryDecodeTonePreviewSwap(pending);
  }, [finalizeTonePreviewSwap, tryDecodeTonePreviewSwap]);

  useEffect(() => {
    tonePreviewSourceRef.current = generatedImages;
    const pending = tonePreviewClearRef.current;
    if (pending) tryDecodeTonePreviewSwap(pending);
  }, [generatedImages, tryDecodeTonePreviewSwap]);

  useEffect(() => () => {
    Object.values(tonePreviewSwapTimeoutsRef.current).forEach(clearTimeout);
    tonePreviewSwapTimeoutsRef.current = {};
  }, []);

  // 已应用色调记录（key: `${taskId}:${index}`），持久化到 localStorage，
  // 刷新/重新打开弹窗后仍显示上次数值，且始终以调色前原图为基准重新渲染
  const [toneRecords, setToneRecords] = useState<Record<string, ToneRecord>>({});
  const toneRecordsRef = useRef<Record<string, ToneRecord>>({});
  toneRecordsRef.current = toneRecords;

  useEffect(() => {
    setToneRecords(loadToneRecords());
  }, []);

  const toneRecordKey = useCallback(
    (idx: number) => `${taskIdRef.current || "local"}:${idx}`,
    [],
  );

  const updateToneRecords = useCallback((
    updates: Array<{ key: string; record: ToneRecord | null }>,
  ) => {
    setToneRecords((prev) => {
      const next = { ...prev };
      for (const update of updates) {
        if (update.record) next[update.key] = update.record;
        else delete next[update.key];
      }
      saveToneRecords(next);
      toneRecordsRef.current = next;
      return next;
    });
  }, []);

  const handleApplyTone = useCallback(async (
    idx: number,
    tone: number,
    scope: "single" | "batch",
  ) => {
    const targets = scope === "single"
      ? imageEditItems.filter((item) => item.index === idx)
      : imageEditItems;
    if (targets.length === 0) {
      setToastMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      throw new Error("E8101");
    }
    const targetIndexes = targets.map((item) => item.index);
    // 单张自动保存和批量应用都在替换图片期间显示 loading，避免用户把切图过程误认为闪烁。
    setToneApplyingIndexes((prev) => {
      const next = new Set(prev);
      targetIndexes.forEach((targetIndex) => next.add(targetIndex));
      return next;
    });
    let waitingForPreviewSwap = false;
    try {
      const jobs = await Promise.all(targets.map(async (item) => {
        const key = toneRecordKey(item.index);
        const record = toneRecordsRef.current[key];
        const baseUrl = record?.sourceUrl || item.url;
        if (tone === 0) {
          // 调回 0：恢复调色前原图，并清除记录
          if (!record) return null;
          return {
            key,
            record: null as ToneRecord | null,
            result: { sourceIndex: item.index, sourceUrl: item.url, editedUrl: baseUrl },
          };
        }
        const blob = await createToneAdjustedBlob(baseUrl, tone);
        const uploaded = await imageEditApi.uploadEditedImage(blob, taskIdRef.current || "", item.index);
        return {
          key,
          record: { tone, sourceUrl: baseUrl, updatedAt: Date.now() } as ToneRecord | null,
          result: { sourceIndex: item.index, sourceUrl: item.url, editedUrl: uploaded.url },
        };
      }));
      const validJobs = jobs.filter((job): job is NonNullable<typeof job> => job !== null);
      if (validJobs.length === 0) return;
      // 快捷调色完成后直接更新图片；单张和批量均不弹成功提示
      await handleImageEditSave(validJobs.map((job) => job.result), { silent: true });
      updateToneRecords(validJobs.map((job) => ({ key: job.key, record: job.record })));
      // Only the visible object-URL preview needs the delayed decode handoff.
      // If preview rendering failed or had not finished before save, there is
      // nothing that can later clear the spinner, so finalize in finally.
      waitingForPreviewSwap = scope === "single" && tonePreviewRef.current?.idx === idx;
    } catch (err) {
      setToastMessage({
        text: getSafeErrorMessage(err, "E8102 图片保存失败，请稍后重试"),
        type: "error",
      });
      throw err;
    } finally {
      if (!waitingForPreviewSwap) {
        setToneApplyingIndexes((prev) => {
          const next = new Set(prev);
          targetIndexes.forEach((targetIndex) => next.delete(targetIndex));
          return next;
        });
      }
    }
  }, [imageEditItems, handleImageEditSave, toneRecordKey, updateToneRecords]);

  // Quick tone controls were removed from the toolbar; retain the persistence
  // pipeline for backwards-compatible saved records without exposing it here.
  void handleTonePreviewChange;
  void handleApplyTone;

  const actionButtonClass = (size: "sm" | "lg" = "sm") => size === "lg"
    ? delivery?.layout === 'tile' ? "flex h-8 w-8 items-center justify-center rounded-lg bg-black/60 text-white hover:text-white" : "w-8 h-8 rounded-lg bg-[url('/button_bg3.png')] bg-cover bg-center bg-no-repeat flex items-center justify-center text-white/80 hover:text-white backdrop-blur-xs transition-[filter,color] hover:brightness-125"
    : "w-7 h-7 rounded bg-[url('/button_bg3.png')] bg-cover bg-center bg-no-repeat flex items-center justify-center text-white/80 hover:text-white transition-[filter,color] hover:brightness-125";

  const renderRefineToolButton = (imgUrl: string, idx: number, size: "sm" | "lg" = "sm") => {
    const cls = actionButtonClass(size);
    return (
      <ActionIconButton
        label="AI重绘"
        size={size}
        className={cls}
        onPointerEnter={() => preloadRefineEditor(imgUrl)}
        onFocus={() => preloadRefineEditor(imgUrl)}
        onPointerDown={() => preloadRefineEditor(imgUrl)}
        onClick={() => handleOpenRefine(imgUrl, idx)}
      >
        <svg className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} viewBox="0 0 1024 1024" aria-hidden="true" fill="currentColor">
          <path d="M826.368 325.632c0-7.168 2.048-10.24 10.24-10.24h123.904c7.168 0 10.24 2.048 10.24 10.24v621.568c0 7.168-2.048 10.24-10.24 10.24h-122.88c-8.192 0-10.24-4.096-10.24-10.24l-1.024-621.568z m-8.192-178.176c0-50.176 35.84-79.872 79.872-79.872 48.128 0 79.872 32.768 79.872 79.872 0 52.224-33.792 79.872-81.92 79.872-46.08 1.024-77.824-27.648-77.824-79.872zM462.848 584.704C441.344 497.664 389.12 307.2 368.64 215.04h-2.048c-16.384 92.16-58.368 247.808-92.16 369.664h188.416zM243.712 712.704l-62.464 236.544c-2.048 7.168-4.096 8.192-12.288 8.192H54.272c-8.192 0-10.24-2.048-8.192-12.288l224.256-783.36c4.096-13.312 7.168-26.624 8.192-65.536 0-6.144 2.048-8.192 7.168-8.192H450.56c6.144 0 8.192 2.048 10.24 8.192l250.88 849.92c2.048 7.168 0 10.24-7.168 10.24H573.44c-7.168 0-10.24-2.048-12.288-7.168l-65.536-236.544c1.024 1.024-251.904 0-251.904 0z" />
        </svg>
      </ActionIconButton>
    );
  };

  const renderColorEditButton = (_imgUrl: string, idx: number, size: "sm" | "lg" = "sm") => {
    const cls = actionButtonClass(size);
    return (
      <ActionIconButton
        label="图像编辑"
        className={cls}
        onClick={() => handleOpenImageEdit(idx)}
      >
        <ImageEditActionIcon className={size === "lg" ? "h-[18px] w-[18px]" : "h-4 w-4"} />
      </ActionIconButton>
    );
  };

  // Determine whether left panel cards should show
  const hasModelImage = !!modelImage && modelImage.length > 0;
  const hasProductImage = !!productImage && productImage.length > 0;
  const hasLeftPanel = hasModelImage || hasProductImage;
  /** 操作栏在图片右侧占位列，与图片并排（非 absolute） */
  const imageActionMediaClass =
    "relative flex-1 min-w-0 h-full max-h-full flex items-center justify-center rounded-lg overflow-hidden bg-white/5 border border-white/10";
  const imageActionMediaClassSingle =
    "relative h-full max-h-full max-w-full shrink flex items-center justify-center rounded-lg overflow-hidden bg-white/5 border border-white/10";
  const imageWithActionsRowClass = "flex h-full max-h-full max-w-full items-start gap-1";
  const imageWithActionsRowClassSingle =
    "inline-flex h-full max-h-full max-w-full items-start justify-center gap-1";
  const imageActionMediaClassCompact =
    "relative flex-1 min-w-0 h-full max-h-full flex items-center justify-center rounded-lg overflow-hidden bg-white/5 border border-white/10";
  const imageWithActionsRowClassCompact =
    "flex h-full max-h-full w-full max-w-full items-start justify-center gap-1";
  const actionColumnClass = "shrink-0 flex flex-col gap-1 pointer-events-auto z-20 self-start";
  const actionColumnClassLg = "shrink-0 flex flex-col gap-1.5 pointer-events-auto z-20 self-start";
  const previewMaxHeight = "calc(100vh - 240px)";
  const fitImageClass = "block max-w-full max-h-full w-auto h-auto object-contain";
  const responsiveContainImageStyle: CSSProperties = {
    maxHeight: "100%",
    maxWidth: "100%",
    width: "auto",
    height: "auto",
  };
  const singlePreviewViewportStyle: CSSProperties = {
    maxHeight: previewMaxHeight,
    height: "100%",
  };
  const singleFitImageStyle: CSSProperties = {
    ...responsiveContainImageStyle,
    maxHeight: previewMaxHeight,
  };

  const leftPanelCardClass =
    "w-full max-w-[140px] sm:max-w-[160px] lg:max-w-[180px] aspect-3/4 rounded-lg overflow-hidden bg-white/5 border border-white/10 shrink-0";
  const effectGridGap = 12;
  const adaptiveEffectListClass = `flex-1 min-h-0 h-full mt-6 flex flex-wrap gap-3 px-[50px] ${
    outputCount > 4
      ? "justify-start items-start content-start overflow-x-hidden overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/18 [&::-webkit-scrollbar-track]:bg-transparent"
      : "justify-center items-center content-center overflow-x-hidden overflow-y-auto"
  }`;
  const getEffectImageRatio = (idx: number): { w: number; h: number } => {
    const loaded = imageDimensions[idx];
    if (loaded && loaded.w > 0 && loaded.h > 0) return loaded;
    // 每槽位独立覆盖（优先 cropSize，其次语义比例），再回退到全局 effectiveRatio
    const slotCrop = outputCropSizes?.[idx] ?? null;
    const slotRatio = aspectRatios?.[idx] ?? null;
    const fromSlot = parseOutputCropSize(slotCrop) ?? (slotRatio ? parseAspectRatio(slotRatio) : null);
    const ratio = fromSlot ?? effectiveRatio;
    return ratio.w > 0 && ratio.h > 0 ? ratio : { w: 3, h: 4 };
  };
  const getAdaptiveEffectBasis = (idx: number, count: number): string => {
    if (count <= 1) return "100%";
    if (count === 2) return `calc((100% - ${effectGridGap}px) / 2)`;
    if (count > 4) return `calc((100% - ${3 * effectGridGap}px) / 4)`;

    const ratio = getEffectImageRatio(idx).w / getEffectImageRatio(idx).h;
    if (ratio >= 2.05) return "100%";
    if (ratio >= 1.1) return `calc((100% - ${effectGridGap}px) / 2)`;
    if (ratio >= 0.72) return count <= 3
      ? `calc((100% - ${2 * effectGridGap}px) / 3)`
      : `calc((100% - ${3 * effectGridGap}px) / 4)`;
    return `calc((100% - ${3 * effectGridGap}px) / 4)`;
  };
  const adaptiveEffectCellStyle = (idx: number, count: number): CSSProperties => {
    if (count >= 3) {
      const colWidth = `calc((100% - ${effectGridGap}px) / 2)`;
      const twoRowCellHeight = `calc((100% - ${effectGridGap}px) / 2)`;
      return {
        flex: "0 0 auto",
        width: colWidth,
        height: twoRowCellHeight,
        alignSelf: "center",
        minHeight: 0,
      };
    }
    if (count <= 4) {
      const displayColumns = count === 2 ? 3 : count;
      const totalColumnGap = Math.max(0, displayColumns - 1) * effectGridGap;
      const colWidth = count <= 1 ? "100%" : `calc((100% - ${totalColumnGap}px) / ${displayColumns})`;
      return {
        flex: "0 1 auto",
        width: colWidth,
        aspectRatio: "2 / 3",
        alignSelf: "center",
        minHeight: 0,
      };
    }
    const basis = getAdaptiveEffectBasis(idx, count);
    return {
      flex: `0 1 ${basis}`,
      aspectRatio: "2 / 3",
      maxWidth: basis,
      minHeight: 0,
    };
  };
  const gridEffectImageClass = "w-full h-full object-contain";

  // 模特生套图布局配置
  const isSuitSet = subOption === "模特生套图";

  const centerOffsetY = 120;

  const frontX = 115;
  const frontY = centerOffsetY;
  const frontW = 200;
  const frontH = 280;

  const backX = 330;
  const backY = centerOffsetY;
  const backW = 200;
  const backH = 280;

  const suitModelX = 115;
  const suitModelY = centerOffsetY + 300;
  const suitModelW = 415;
  const suitModelH = 280;

  const suitEffectX = 550;
  const suitEffectY = centerOffsetY;
  const suitEffectGridW = 600;

  const isModelToImage = subOption === "模特图生图";
  const lowerCardLabel = isModelToImage ? "背景图" : "产品图";
  const lowerCardImage = isModelToImage ? backgroundImage : productImage;
  // 与详情页复刻 ResultColumnGroup 保持同一紧凑结果标签视觉合同。
  const resultSectionLabelClass = "inline-flex h-5 box-border min-w-[58px] shrink-0 self-start items-center justify-center px-[11px] whitespace-nowrap bg-[url('/button_bg9.png')] bg-[length:100%_100%] bg-center bg-no-repeat text-center text-[12px] leading-none text-[#E6E6E6]";
  const cardLabelClass = `absolute -top-2 left-3 z-10 ${resultSectionLabelClass}`;
  const sectionLabelClass = `absolute top-1 left-3 z-30 pointer-events-none ${resultSectionLabelClass}`;
  const hasNextStep = (nextStepTargets?.length ?? 0) > 0 && status === "completed";

  const renderSectionLabel = (text: ReactNode) => (
    <div className={sectionLabelClass}>
      {text}
    </div>
  );

  const hoverPreviewOverlay = hoverPreviewUrl ? (
    <div
      data-ecom-upload-hover-preview
      className="pointer-events-none absolute left-3 top-4 z-40 inline-flex overflow-hidden"
    >
      <SafeImage
        src={hoverPreviewUrl}
        alt={uploadHoverPreview?.alt || "上传图片预览"}
        className="block h-auto w-auto max-h-[min(360px,50vh)] max-w-[min(320px,42vw)] object-contain"
        decoding="async"
      />
    </div>
  ) : null;

  const withHoverPreviewOverlay = (content: ReactNode) => (
    <div className="relative h-full w-full">
      {content}
      {hoverPreviewOverlay}
    </div>
  );

  const renderLeftPanelCards = () => (
    <>
      {hasModelImage && (
        <div className="relative pt-3 mt-2">
          <div className={cardLabelClass}>
            模特图
          </div>
          <div
            className={`${leftPanelCardClass} cursor-zoom-in`}
            onClick={() => onPreview?.(modelImage!)}
          >
            <SafeImage src={modelImage!} alt="模特图" className="w-full h-full object-contain" decoding="async" />
          </div>
        </div>
      )}
      {(isModelToImage ? !!backgroundImage : hasProductImage) && (
        <div className="relative pt-3 mt-2">
          <div className={cardLabelClass}>
            {lowerCardLabel}
          </div>
          <div
            className={`${leftPanelCardClass} cursor-zoom-in`}
            onClick={() => lowerCardImage && onPreview?.(lowerCardImage)}
          >
            <SafeImage src={lowerCardImage!} alt={lowerCardLabel} className="w-full h-full object-contain" decoding="async" />
          </div>
        </div>
      )}
    </>
  );

  const renderStandardShell = (effectArea: ReactNode) => (
    <div className="h-full flex flex-col overflow-hidden p-2 sm:p-3">
      {selectModeBar}
      <div className={`flex-1 min-h-0 flex ${hasLeftPanel ? "flex-col lg:flex-row" : "flex-col"} gap-2 sm:gap-3 overflow-hidden`}>
        {hasLeftPanel && (
          <div className="shrink-0 flex flex-row lg:flex-col gap-2 sm:gap-3 lg:w-auto overflow-x-auto lg:overflow-visible pb-1 lg:pb-0">
            {renderLeftPanelCards()}
          </div>
        )}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {effectArea}
        </div>
      </div>
    </div>
  );

  // 图片勾选状态管理
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());

  // 批量下载菜单和选择模式
  const [isSelectMode, setIsSelectMode] = useState(false);

  useEffect(() => {
    if (status === "completed" && generatedImages.length > 0) {
      const validIndexes = new Set<number>();
      generatedImages.forEach((img, idx) => {
        if (img) validIndexes.add(idx);
      });
      setSelectedIndexes(validIndexes);
    }
  }, [status, generatedImages]);

  const toggleSelect = (idx: number) => {
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // 批量下载相关函数
  const handleDownloadAll = async () => {
    const validImages = generatedImages.filter(Boolean);
    if (validImages.length === 0) return;
    try {
      await downloadFiles(validImages, "flowx");
      setToastMessage({
        text: validImages.length > 1
          ? `已打包下载 ${validImages.length} 张图片`
          : "下载成功",
        type: "success",
      });
    } catch {
      setToastMessage({ text: "批量下载失败，请稍后重试", type: "error" });
    }
  };

  const handleEnterSelectMode = () => {
    setIsSelectMode(true);
    // 默认全选所有有效图片
    const allValidIndexes = generatedImages.map((img, idx) => img ? idx : -1).filter(idx => idx >= 0);
    setSelectedIndexes(new Set(allValidIndexes));
  };

  const handleConfirmSelectedDownload = async () => {
    const selectedUrls = Array.from(selectedIndexes)
      .sort((a, b) => a - b)
      .map(idx => generatedImages[idx])
      .filter(Boolean);

    if (selectedUrls.length === 0) {
      setToastMessage({ text: "请至少选择一张图片", type: "error" });
      return;
    }

    try {
      await downloadFiles(selectedUrls, "flowx");
      setToastMessage({
        text: selectedUrls.length > 1
          ? `已打包下载 ${selectedUrls.length} 张图片`
          : "下载成功",
        type: "success",
      });
    } catch {
      setToastMessage({ text: "下载失败，请稍后重试", type: "error" });
      return;
    }
    setIsSelectMode(false);
    setSelectedIndexes(new Set());
  };

  const handleCancelSelectMode = () => {
    setIsSelectMode(false);
    setSelectedIndexes(new Set());
  };

  const isMultiOutput = outputCount > 1 && generatedImages.filter(Boolean).length > 1;
  const selectedImages = isMultiOutput
    ? generatedImages.filter((_, idx) => selectedIndexes.has(idx)).filter(Boolean)
    : (imageUrl && isMediaUrl(imageUrl) && !isVideoUrl(imageUrl)) ? [imageUrl] : [];

  const renderImageActions = (imgUrl: string, idx: number, size: "sm" | "lg" = "sm") => (
    <div className={delivery ? "shrink-0 flex flex-col gap-2 pointer-events-auto self-start" : size === "lg" ? actionColumnClassLg : actionColumnClass}>
      <ImageActionToolbar
        size={size}
        overlay={delivery?.layout === 'tile'}
        showBatchActions={isMultiOutput}
        onSingleDownload={!delivery || delivery.actions.includes('download') ? () => handleSingleDownload(imgUrl) : undefined}
        onPreview={delivery?.layout === 'tile' && onPreview ? () => onPreview(imgUrl, 0) : undefined}
        onRegenerate={
          onRegenerateSingle
            ? () => onRegenerateSingle(idx)
            : onRegenerate
        }
        onFavorite={onFavorite ? () => onFavorite(imgUrl) : undefined}
        onPublish={onPublish ? () => onPublish(imgUrl) : undefined}
        onDownloadAll={isMultiOutput ? handleDownloadAll : undefined}
        onEnterSelectMode={isMultiOutput ? handleEnterSelectMode : undefined}
        onAutoStitch={isMultiOutput ? onAutoStitch : undefined}
        leading={
          <>
            {(!delivery || delivery.actions.includes('repaint')) && renderRefineToolButton(imgUrl, idx, size)}
            {(!delivery || delivery.actions.includes('edit')) && renderColorEditButton(imgUrl, idx, size)}
          </>
        }
      />
    </div>
  );

  const wrapImageWithActions = (
    media: ReactNode,
    actions: ReactNode | null,
    layout: "single" | "grid" = "grid",
  ) => {
    if (!actions) return <>{media}</>;
    const isCompactGrid = layout === "grid" && outputCount >= 3;
    const rowClass = layout === "single"
      ? imageWithActionsRowClassSingle
      : isCompactGrid
        ? imageWithActionsRowClassCompact
        : imageWithActionsRowClass;
    const mediaClass = layout === "single"
      ? imageActionMediaClassSingle
      : isCompactGrid
        ? imageActionMediaClassCompact
        : imageActionMediaClass;
    return (
      <div className={rowClass} style={layout === "single" ? singlePreviewViewportStyle : undefined}>
        <div className={mediaClass}>{media}</div>
        {actions}
      </div>
    );
  };

  const refineEditorModal = refineEditorOpen ? (
    <ImageRefineEditor
      imageUrl={refineImageUrl}
      taskId={taskId || ""}
      imageIndex={refineImageIndex}
      productImage={productImage || undefined}
      onClose={() => setRefineEditorOpen(false)}
      onRefineStart={handleRefineStart}
    />
  ) : null;

  const imageEditModal = imageEditOpen ? (
    <ImageEditModal
      open={imageEditOpen}
      mode={imageEditMode}
      images={imageEditItems}
      initialImageId={imageEditInitialId}
      taskId={taskId}
      saveAsCopy={Boolean(delivery)}
      onClose={() => setImageEditOpen(false)}
      onSave={async (results) => {
        await handleImageEditSave(results);
        // 大调色弹窗保存后图片内容已变化，清除对应的快捷色调记录，
        // 避免后续快捷调色以旧原图为基准覆盖大弹窗的编辑结果
        updateToneRecords(results.map((result) => ({
          key: toneRecordKey(result.sourceIndex),
          record: null,
        })));
      }}
    />
  ) : null;

  const recognitionModal = recognitionImageUrl ? (
    <ImageLayerRecognitionModal
      imageUrl={recognitionImageUrl}
      images={imageEditItems}
      initialImageId={imageEditItems.find((item) => item.url === recognitionImageUrl)?.id}
      initialTask={recognitionTask || undefined}
      onClose={() => { setRecognitionImageUrl(""); setRecognitionTask(null); }}
    />
  ) : null;

  const toastEl = toastMessage ? (
    <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-200 px-5 py-2.5 rounded-lg text-sm font-medium shadow-lg backdrop-blur-xs animate-[fadeInDown_0.3s_ease-out] ${
      toastMessage.type === "success"
        ? "bg-green-500/90 text-white"
        : "bg-red-500/90 text-white"
    }`}>
      {toastMessage.text}
    </div>
  ) : null;

  // Use activeRefines directly (synchronized from page-level tracker) to avoid
  // stale refiningIndexes persisting after a history record switch
  const renderRefineOverlay = (idx: number) => {
    // Local state is set synchronously by the editor for both __pending__ and
    // real task IDs. The page-owned map is the persistence/reload source.
    if ((activeRefines && idx in activeRefines) || idx in refiningIndexes) {
      return <RefineOverlay visible={true} startedAt={activeRefines?.[idx]?.startedAt} />;
    }
    if (toneApplyingIndexes.has(idx)) {
      return (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-black/35">
          <Loader2 className="h-8 w-8 animate-spin text-white" aria-label="调色应用中" />
        </div>
      );
    }
    return null;
  };

  const renderGridEffectImageMedia = (
    src: string,
    idx: number,
    onPreviewClick?: () => void,
    extra?: ReactNode,
  ) => (
    <>
      <div
        className="relative flex h-full w-full min-h-0 min-w-0 items-center justify-center cursor-zoom-in"
        onClick={onPreviewClick}
      >
        <SafeImage
          onLoad={(e) => handleBatchImgLoad(e, idx)}
          src={tonePreview?.idx === idx ? tonePreview.url : src}
          alt={`效果图 ${idx + 1}`}
          style={responsiveContainImageStyle}
          className={gridEffectImageClass}
          decoding="async"
        />
        {extra}
        {renderRefineOverlay(idx)}
      </div>
      {outputCount > 1 && (
        <div className="absolute bottom-2 left-2 bg-black/60 text-white/80 text-xs px-1.5 py-0.5 rounded">
          {idx + 1}/{outputCount}
        </div>
      )}
    </>
  );

  const renderSingleFitImage = (
    src: string,
    alt: string,
    idx: number,
    onPreviewClick?: () => void,
  ) =>
    wrapImageWithActions(
      <>
        <div className="relative flex h-full w-full min-h-0 min-w-0 items-center justify-center">
          <SafeImage
            src={tonePreview?.idx === idx ? tonePreview.url : src}
            alt={alt}
            style={responsiveContainImageStyle}
            className={fitImageClass}
            decoding="async"
          />
          {onPreviewClick && (
            <div className="absolute inset-0 cursor-zoom-in" onClick={onPreviewClick} />
          )}
          {renderRefineOverlay(idx)}
        </div>
      </>,
      renderImageActions(src, idx, "lg"),
      "single",
    );

  const selectModeBar = isSelectMode ? (
    <div className="sticky top-0 z-20 flex justify-end px-6 pt-3 pb-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-white/50">
          已选择 {selectedIndexes.size}/{generatedImages.filter(Boolean).length} 张
        </span>
        <button
          type="button"
          onClick={handleCancelSelectMode}
          className="flex h-8 items-center gap-1.5 rounded-lg border border-white/10 bg-white/10 px-3 text-xs text-white/70 transition-colors hover:bg-white/15 hover:text-white"
        >
          取消
        </button>
        <button
          type="button"
          onClick={handleConfirmSelectedDownload}
          disabled={selectedIndexes.size === 0}
          className="relative h-8 overflow-hidden rounded-md border-none px-4 text-xs font-medium text-[#00FAE5] transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-[url('/buttonBg.png')] bg-[length:100%_100%] bg-center bg-no-repeat"
          />
          <span className="relative z-10">
            下载已选({selectedIndexes.size})
          </span>
        </button>
      </div>
    </div>
  ) : null;

  // Empty State
  if (status === "empty") {
    return (
      <>
        {withHoverPreviewOverlay(<div className="h-full relative flex items-center justify-center pb-40">
          <div className="text-center">
            <img src="/no_data.png" alt="" className="w-24 h-24 mx-auto mb-4" />
            <span className="text-base text-white/40">选择素材开始生成</span>
          </div>
        </div>)}
      {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
      </>
    );
  }

  // Failed State
  if (status === "failed") {
    if (isSuitSet) {
      return (
        <>
          {withHoverPreviewOverlay(<div className="h-full relative overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent will-change-transform transform-[translateZ(0)]">
                        {/* 产品正面图 */}
            <div className="absolute" style={{ left: `${frontX}px`, top: `${frontY}px`, width: `${frontW}px`, height: `${frontH}px` }}>
              <div className={cardLabelClass}>
                产品正面图
              </div>
              <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {productFrontImage ? (
                  <SafeImage src={productFrontImage} alt="产品正面图" className="w-full h-full object-contain" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无产品正面图</div>
                )}
              </div>
            </div>
            {/* 产品背面图 */}
            <div className="absolute" style={{ left: `${backX}px`, top: `${backY}px`, width: `${backW}px`, height: `${backH}px` }}>
              <div className={cardLabelClass}>
                产品背面图
              </div>
              <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {productBackImage ? (
                  <SafeImage src={productBackImage} alt="产品背面图" className="w-full h-full object-contain" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无产品背面图</div>
                )}
              </div>
            </div>
            {/* 效果图区域 - 失败状态 */}
            <div className="absolute" style={{ left: `${suitEffectX}px`, top: `${suitEffectY}px`, width: `${suitEffectGridW}px` }}>
              <div className={cardLabelClass}>
                效果图
              </div>
              <div className={adaptiveEffectListClass}>
                {Array.from({ length: outputCount }).map((_, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center" style={adaptiveEffectCellStyle(idx, outputCount)}>
                    <FailedCard
                      errorMessage={idx === 0 ? errorMessage : undefined}
                      onRetry={onRegenerateSingle ? () => onRegenerateSingle(idx) : onRegenerate}
                      size={failSlotSize(outputCount)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>)}
                {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
        </>
      );
    }

    return (
      <>
        {withHoverPreviewOverlay(renderStandardShell(
          <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
            {renderSectionLabel(<>效果图{outputCount > 1 ? ` (${outputCount}张)` : ""}</>)}
            {outputCount <= 1 ? (
              <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto p-2">
                <div className="rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center" style={singleGeneratingCardStyle}>
                  <FailedCard errorMessage={errorMessage} onRetry={onRegenerateSingle ? () => onRegenerateSingle(0) : onRegenerate} />
                </div>
              </div>
            ) : (
              <div className={adaptiveEffectListClass}>
                {Array.from({ length: outputCount }).map((_, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center min-h-0 w-full"
                    style={adaptiveEffectCellStyle(idx, outputCount)}
                  >
                    <FailedCard
                      errorMessage={idx === 0 ? errorMessage : undefined}
                      onRetry={onRegenerateSingle ? () => onRegenerateSingle(idx) : onRegenerate}
                      size={failSlotSize(outputCount)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
              {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
      </>
    );
  }

  // Generating State
  if (status === "generating") {
    if (isSuitSet) {
      return (
        <>
          {withHoverPreviewOverlay(<div className="h-full relative overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent will-change-transform transform-[translateZ(0)]">
                        {/* 产品正面图 */}
            <div className="absolute" style={{ left: `${frontX}px`, top: `${frontY}px`, width: `${frontW}px`, height: `${frontH}px` }}>
              <div className={cardLabelClass}>
                产品正面图
              </div>
              <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {productFrontImage ? (
                  <SafeImage src={productFrontImage} alt="产品正面图" className="w-full h-full object-contain" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无产品正面图</div>
                )}
              </div>
            </div>
            {/* 产品背面图 */}
            <div className="absolute" style={{ left: `${backX}px`, top: `${backY}px`, width: `${backW}px`, height: `${backH}px` }}>
              <div className={cardLabelClass}>
                产品背面图
              </div>
              <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {productBackImage ? (
                  <SafeImage src={productBackImage} alt="产品背面图" className="w-full h-full object-contain" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无产品背面图</div>
                )}
              </div>
            </div>
            {/* 模特图 */}
            <div className="absolute" style={{ left: `${suitModelX}px`, top: `${suitModelY}px`, width: `${suitModelW}px`, height: `${suitModelH}px` }}>
              <div className={cardLabelClass}>
                模特图
              </div>
              <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {modelImage ? (
                  <SafeImage src={modelImage} alt="模特图" className="w-full h-full object-contain" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无模特图</div>
                )}
              </div>
            </div>
            {/* 效果图网格 - 生成中 */}
            <div className="absolute" style={{ left: `${suitEffectX}px`, top: `${suitEffectY}px`, width: `${suitEffectGridW}px` }}>
              <div className={cardLabelClass}>
                效果图 ({outputCount}张)
              </div>
              <div className={adaptiveEffectListClass}>
                {Array.from({ length: outputCount }).map((_, idx) => {
                  const cardProgress = taskProgresses.length > 0 ? (taskProgresses[idx] ?? progress) : progress;
                  return (
                    <div key={idx} className="relative w-full min-h-0 flex items-start justify-center overflow-hidden" style={adaptiveEffectCellStyle(idx, outputCount)}>
                      {regeneratingIndexes.includes(idx) ? (
                        <div className="w-full h-full relative flex items-center justify-center">
                          {generatedImages[idx] && <SafeImage src={generatedImages[idx]} alt="" className="max-w-full max-h-full w-auto h-auto object-contain opacity-20" decoding="async" />}
                          <div className="absolute inset-0 flex flex-col items-center justify-center"
                            style={{ background: "linear-gradient(135deg, rgba(0,250,229,0.16) 0%, rgba(56,232,198,0.14) 50%, rgba(184,255,233,0.12) 100%)" }}>
                            <div className="w-8 h-8 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-2" />
                            <span className="text-xs text-white/60">重新生成中...</span>
                          </div>
                        </div>
                      ) : failedIndexes.includes(idx) && !generatedImages[idx] ? (
                        <FailedCard
                          onRetry={onRegenerateSingle ? () => onRegenerateSingle(idx) : undefined}
                          size={failSlotSize(outputCount)}
                        />
                      ) : generatedImages[idx] ? (
                        wrapImageWithActions(
                          renderGridEffectImageMedia(
                            generatedImages[idx],
                            idx,
                            () => onPreview?.(generatedImages[idx], idx),
                          ),
                          renderImageActions(generatedImages[idx], idx),
                        )
                      ) : (
                        <GeneratingCard progress={cardProgress} size={outputCount <= 1 ? "large" : "small"} statusHint={statusHint} startedAt={generationStartedAt} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>)}
                {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
        </>
      );
    }

    return (
      <>
        {withHoverPreviewOverlay(renderStandardShell(
          <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
            {renderSectionLabel(<>效果图 ({outputCount}张)</>)}
            {outputCount <= 1 ? (
              <div className="flex-1 min-h-0 w-full h-full flex items-center justify-center overflow-hidden p-2">
                {generatedImages[0] &&
                !regeneratingIndexes.includes(0) &&
                !(failedIndexes.includes(0) && !generatedImages[0]) ? (
                  renderSingleFitImage(
                    generatedImages[0],
                    "效果图",
                    0,
                    () => onPreview?.(generatedImages[0], 0),
                  )
                ) : (
                  <div
                    className={imageActionMediaClassSingle}
                    style={singleGeneratingCardStyle}
                  >
                    {regeneratingIndexes.includes(0) ? (
                      <div className="relative inline-flex max-w-full" style={singleFitImageStyle}>
                        {generatedImages[0] && (
                          <SafeImage src={generatedImages[0]} alt="" style={singleFitImageStyle} className={`${fitImageClass} opacity-20`} decoding="async" />
                        )}
                        <div className="absolute inset-0 flex flex-col items-center justify-center"
                          style={{ background: "linear-gradient(135deg, rgba(0,250,229,0.16) 0%, rgba(56,232,198,0.14) 50%, rgba(184,255,233,0.12) 100%)" }}>
                          <div className="w-8 h-8 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-2" />
                          <span className="text-xs text-white/60">重新生成中...</span>
                        </div>
                      </div>
                    ) : failedIndexes.includes(0) && !generatedImages[0] ? (
                      <FailedCard
                        onRetry={onRegenerateSingle ? () => onRegenerateSingle(0) : onRegenerate}
                        size="large"
                      />
                    ) : (
                      <div className="max-w-full" style={singleGeneratingCardStyle}>
                        <GeneratingCard progress={taskProgresses.length > 0 ? (taskProgresses[0] ?? progress) : progress} size="large" statusHint={statusHint} startedAt={generationStartedAt} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className={adaptiveEffectListClass}>
                {Array.from({ length: outputCount }).map((_, idx) => {
                  const cardProgress = taskProgresses.length > 0 ? (taskProgresses[idx] ?? progress) : progress;
                  return (
                    <div key={idx} className="relative w-full min-h-0 flex items-start justify-center overflow-hidden" style={adaptiveEffectCellStyle(idx, outputCount)}>
                      {regeneratingIndexes.includes(idx) ? (
                        <div className="w-full h-full relative">
                          {generatedImages[idx] && <SafeImage src={generatedImages[idx]} alt="" className="max-w-full max-h-full w-auto h-auto object-contain opacity-20" decoding="async" />}
                          <div className="absolute inset-0 flex flex-col items-center justify-center"
                            style={{ background: "linear-gradient(135deg, rgba(0,250,229,0.16) 0%, rgba(56,232,198,0.14) 50%, rgba(184,255,233,0.12) 100%)" }}>
                            <div className="w-8 h-8 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-2" />
                            <span className="text-xs text-white/60">重新生成中...</span>
                          </div>
                        </div>
                      ) : failedIndexes.includes(idx) && !generatedImages[idx] ? (
                        <FailedCard
                          onRetry={onRegenerateSingle ? () => onRegenerateSingle(idx) : undefined}
                          size={failSlotSize(outputCount)}
                        />
                      ) : generatedImages[idx] ? (
                        wrapImageWithActions(
                          renderGridEffectImageMedia(
                            generatedImages[idx],
                            idx,
                            () => onPreview?.(generatedImages[idx], idx),
                          ),
                          renderImageActions(generatedImages[idx], idx),
                        )
                      ) : (
                        <GeneratingCard progress={cardProgress} size={outputCount <= 1 ? "large" : "small"} statusHint={statusHint} startedAt={generationStartedAt} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
              {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
      </>
    );
  }

  // Completed State - 模特生套图布局
  if (isSuitSet) {
    return (
      <>
        {withHoverPreviewOverlay(<div className="h-full relative overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent will-change-transform transform-[translateZ(0)]">
          {selectModeBar}
          {/* 产品正面图 */}
          <div className="absolute" style={{ left: `${frontX}px`, top: `${frontY}px`, width: `${frontW}px`, height: `${frontH}px` }}>
            <div className={cardLabelClass}>
              产品正面图
            </div>
            <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
              {productFrontImage ? (
                <SafeImage src={productFrontImage} alt="产品正面图" className="w-full h-full object-contain" decoding="async" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无产品正面图</div>
              )}
            </div>
          </div>
          {/* 产品背面图 */}
          <div className="absolute" style={{ left: `${backX}px`, top: `${backY}px`, width: `${backW}px`, height: `${backH}px` }}>
            <div className={cardLabelClass}>
              产品背面图
            </div>
            <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
              {productBackImage ? (
                <SafeImage src={productBackImage} alt="产品背面图" className="w-full h-full object-contain" decoding="async" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无产品背面图</div>
              )}
            </div>
          </div>
          {/* 模特图 */}
          <div className="absolute" style={{ left: `${suitModelX}px`, top: `${suitModelY}px`, width: `${suitModelW}px`, height: `${suitModelH}px` }}>
            <div className={cardLabelClass}>
              模特图
            </div>
            <div className="w-full h-full rounded-lg overflow-hidden bg-white/5 border border-white/10">
              {modelImage ? (
                <SafeImage src={modelImage} alt="模特图" className="w-full h-full object-contain" decoding="async" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">无模特图</div>
              )}
            </div>
          </div>
          {/* 效果图网格 - 完成状态 */}
          <div className="absolute" style={{ left: `${suitEffectX}px`, top: `${suitEffectY}px`, width: `${suitEffectGridW}px` }}>
            <div className={cardLabelClass}>
              效果图 ({generatedImages.filter(Boolean).length}/{outputCount}张)
            </div>
            <div className={adaptiveEffectListClass}>
              {Array.from({ length: outputCount }).map((_, idx) => (
                <div key={idx} className="relative w-full min-h-0 flex items-start justify-center overflow-hidden" style={adaptiveEffectCellStyle(idx, outputCount)}>
                  {regeneratingIndexes.includes(idx) ? (
                    <div className={`${imageActionMediaClass} w-full h-full relative`}>
                      {generatedImages[idx] && <SafeImage src={generatedImages[idx]} alt="" className="max-w-full max-h-full w-auto h-auto object-contain opacity-20" decoding="async" />}
                      <div className="absolute inset-0 flex flex-col items-center justify-center"
                        style={{ background: "linear-gradient(135deg, rgba(0,250,229,0.16) 0%, rgba(56,232,198,0.14) 50%, rgba(184,255,233,0.12) 100%)" }}>
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-2" />
                        <span className="text-xs text-white/60">重新生成中...</span>
                      </div>
                    </div>
                  ) : failedIndexes.includes(idx) && !generatedImages[idx] ? (
                    <div className={`${imageActionMediaClass} w-full h-full`}>
                      <FailedCard onRetry={onRegenerateSingle ? () => onRegenerateSingle(idx) : undefined} size={failSlotSize(outputCount)} />
                    </div>
                  ) : generatedImages[idx] ? (
                    wrapImageWithActions(
                      renderGridEffectImageMedia(
                        generatedImages[idx],
                        idx,
                        () => onPreview?.(generatedImages[idx], idx),
                        isSelectMode && generatedImages[idx] ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSelect(idx); }}
                            className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all z-10 ${
                              selectedIndexes.has(idx)
                                ? "bg-white border-white shadow-[0_1px_4px_rgba(0,0,0,0.2)]"
                                : "bg-white border-white/80 shadow-[0_1px_4px_rgba(0,0,0,0.15)]"
                            }`}
                          >
                            {selectedIndexes.has(idx) && (
                              <svg className="w-3.5 h-3.5 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>
                        ) : null,
                      ),
                      renderImageActions(generatedImages[idx], idx),
                    )
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <svg className="w-6 h-6 mb-1 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                      </svg>
                      <span className="text-white/30 text-xs">未获取到结果</span>
                      {onRegenerateSingle && (
                        <button onClick={() => onRegenerateSingle(idx)} className="mt-2 px-3 py-1 rounded bg-white/10 hover:bg-white/15 text-xs text-white/60 hover:text-white transition-all">
                          重试
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>)}
              {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
      </>
    );
  }

  // Completed State - 原有布局

  if (delivery?.layout && status === 'completed') {
    return <div className="relative min-w-0" data-ecom-delivery-image data-ecom-delivery-layout={delivery.layout}>
      <div className="group/detail-image relative overflow-hidden bg-[#191919]" data-ecom-delivery-frame>
        <SafeImage src={imageUrl || ''} alt={`正式交付图片 ${delivery.index}`}
          className="block h-auto w-full cursor-zoom-in"
          onClick={() => onPreview?.(imageUrl || '', 0)} />
        {renderRefineOverlay(0)}
        {delivery.layout === 'tile' && <>
          <span className="absolute left-2 top-2 flex h-6 min-w-12 items-center justify-center rounded-full bg-black/60 px-3 text-[12px] leading-6 text-[#F6F6F6]" data-ecom-delivery-index>
            {delivery.index}/{delivery.total}
          </span>
          <div className="pointer-events-none absolute bottom-2 right-2 top-2 overflow-y-auto opacity-0 group-hover/detail-image:pointer-events-auto group-hover/detail-image:opacity-100 group-focus-within/detail-image:pointer-events-auto group-focus-within/detail-image:opacity-100" data-ecom-detail-toolbar>
            {renderImageActions(imageUrl || '', 0, 'lg')}
          </div>
        </>}
      </div>
      {delivery.layout === 'stack' && delivery.actions.includes('download') && <div className="absolute left-[calc(100%+10px)] top-0">
        {renderImageActions(imageUrl || '', 0, 'lg')}
      </div>}
      {refineEditorModal}{recognitionModal}{imageEditModal}{toastEl}
    </div>;
  }

  if (delivery && status === 'completed') {
    // The result browser owns its Figma geometry; other previews keep their layout.
    return <div className="min-w-0 h-[var(--ecom-delivery-row-height)]" data-ecom-delivery-image>
      <div className="flex h-full w-full min-w-0 items-start gap-[10px]">
        <div className="flex h-full min-w-0 flex-1 items-center justify-center" data-ecom-delivery-stage>
          <div className="relative max-w-full overflow-hidden rounded-lg bg-[#191919]">
            <SafeImage src={imageUrl || ''} alt={`正式交付图片 ${delivery.index}`}
              className={`${fitImageClass} cursor-zoom-in`}
              style={{ ...responsiveContainImageStyle, maxHeight: 'var(--ecom-delivery-row-height)' }}
              onClick={() => onPreview?.(imageUrl || '', 0)} />
            <span className="absolute bottom-[15px] left-[15px] flex h-6 min-w-12 items-center justify-center rounded-[43px] bg-black/60 px-3 text-[12px] leading-6 text-[#F6F6F6]"
              data-ecom-delivery-index>
              {delivery.index}/{delivery.total}
            </span>
            {renderRefineOverlay(0)}
          </div>
        </div>
        {delivery.actions.includes('download') && (delivery.toolbar?.target ? <>
          <div className="w-8 shrink-0" aria-hidden="true" />
          {createPortal(<div data-agent-toolbar-current={delivery.toolbar.current ? 'true' : 'false'}
            inert={!delivery.toolbar.current || delivery.toolbar.transitioning}
            aria-hidden={!delivery.toolbar.current || undefined}>
            {renderImageActions(imageUrl || '', 0, 'lg')}
          </div>, delivery.toolbar.target)}
        </> : renderImageActions(imageUrl || '', 0, 'lg'))}
      </div>
      {refineEditorModal}{recognitionModal}{imageEditModal}{toastEl}
    </div>;
  }

  return (
    <>
      {withHoverPreviewOverlay(renderStandardShell(
        <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
          {renderSectionLabel(<>效果图{outputCount > 1 ? ` (${generatedImages.filter(Boolean).length}/${outputCount}张)` : ""}</>)}
          {outputCount <= 1 ? (
            <div className="flex-1 min-h-0 w-full h-full flex items-center justify-center overflow-hidden p-2">
              {(() => {
                const displayUrl =
                  imageUrl && isMediaUrl(imageUrl) && !isVideoUrl(imageUrl)
                    ? imageUrl
                    : generatedImages[0] && isMediaUrl(generatedImages[0]) && !isVideoUrl(generatedImages[0])
                      ? generatedImages[0]
                      : "";
                if (displayUrl) {
                  return renderSingleFitImage(displayUrl, "效果图", 0, () => onPreview?.(displayUrl, 0));
                }
                if (imageUrl && isVideoUrl(imageUrl)) {
                  return (
                    <div className={imageActionMediaClass}>
                      <video src={imageUrl} controls style={singleFitImageStyle} className={fitImageClass} />
                    </div>
                  );
                }
                if (imageUrl) {
                  return (
                    <div className={imageActionMediaClass} style={singleFitImageStyle}>
                      <div className="w-full min-h-[240px] flex flex-col items-center justify-center px-4" style={{ borderRadius: "9.23px", background: "linear-gradient(135deg, #2D1B2E 0%, #1B1025 50%, #1A0A1E 100%)" }}>
                        <div className="w-16 h-16 mb-3 rounded-full bg-amber-500/10 flex items-center justify-center">
                          <svg className="w-8 h-8 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          </svg>
                        </div>
                        <span className="text-amber-400 font-medium text-sm">内容审核未通过</span>
                        <span className="text-xs text-white/40 mt-1 mb-2 max-w-[80%] text-center">提示词或参考图可能包含违规内容，请修改后重试</span>
                        {onRegenerate && (
                          <button onClick={onRegenerate} className="mt-3 px-5 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-xs text-white/70 hover:text-white transition-all border border-white/10">
                            重新生成
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <div className={imageActionMediaClass} style={singleFitImageStyle}>
                    <div className="w-full min-h-[160px] flex items-center justify-center text-white/30 text-sm">无效果图</div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className={adaptiveEffectListClass}>
              {Array.from({ length: outputCount }).map((_, idx) => (
                <div key={idx} className="relative w-full min-h-0 flex items-start justify-center overflow-hidden" style={adaptiveEffectCellStyle(idx, outputCount)}>
                  {regeneratingIndexes.includes(idx) ? (
                    <div className={`${imageActionMediaClass} w-full h-full relative`}>
                      {generatedImages[idx] && <SafeImage src={generatedImages[idx]} alt="" className="max-w-full max-h-full w-auto h-auto object-contain opacity-20" decoding="async" />}
                      <div className="absolute inset-0 flex flex-col items-center justify-center"
                        style={{ background: "linear-gradient(135deg, rgba(0,250,229,0.16) 0%, rgba(56,232,198,0.14) 50%, rgba(184,255,233,0.12) 100%)" }}>
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-2" />
                        <span className="text-xs text-white/60">重新生成中...</span>
                      </div>
                    </div>
                  ) : failedIndexes.includes(idx) && !generatedImages[idx] ? (
                    <div className={`${imageActionMediaClass} w-full h-full`}>
                      <FailedCard onRetry={onRegenerateSingle ? () => onRegenerateSingle(idx) : undefined} size={failSlotSize(outputCount)} />
                    </div>
                  ) : generatedImages[idx] ? (
                    wrapImageWithActions(
                      renderGridEffectImageMedia(
                        generatedImages[idx],
                        idx,
                        () => onPreview?.(generatedImages[idx], idx),
                        isSelectMode && generatedImages[idx] ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSelect(idx); }}
                            className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all z-10 ${
                              selectedIndexes.has(idx)
                                ? "bg-white border-white shadow-[0_1px_4px_rgba(0,0,0,0.2)]"
                                : "bg-white border-white/80 shadow-[0_1px_4px_rgba(0,0,0,0.15)]"
                            }`}
                          >
                            {selectedIndexes.has(idx) && (
                              <svg className="w-3.5 h-3.5 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>
                        ) : null,
                      ),
                      renderImageActions(generatedImages[idx], idx),
                    )
                  ) : (
                    <div className={`${imageActionMediaClass} w-full h-full flex flex-col items-center justify-center`}>
                      <svg className="w-6 h-6 mb-1 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                      </svg>
                      <span className="text-white/30 text-xs">未获取到结果</span>
                      {onRegenerateSingle && (
                        <button onClick={() => onRegenerateSingle(idx)} className="mt-2 px-3 py-1 rounded bg-white/10 hover:bg-white/15 text-xs text-white/60 hover:text-white transition-all">
                          重试
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {hasNextStep && (
            <div className="shrink-0 mt-2 pt-2">
              <div className="flex flex-wrap justify-center gap-2">
                {nextStepTargets!.map((target) => (
                  <button
                    key={`${target.target_workflow_key}-${target.target_coze_param_key}`}
                    disabled={selectedImages.length === 0}
                    onClick={() => onNextStep?.(target, selectedImages)}
                    className={`h-9 min-w-[180px] rounded-full border text-white tracking-[0.2px]
                               flex items-center justify-center gap-1.5 px-5 transition-all
                               bg-[url('/button_bg4.png')] bg-[length:100%_100%] bg-center bg-no-repeat
                               ${selectedImages.length === 0
                                 ? "border-white/10 opacity-50 cursor-not-allowed"
                                 : "border-white/18 shadow-[0_8px_22px_rgba(0,0,0,0.22)] hover:brightness-125 active:scale-[0.99]"
                               }`}
                  >
                    <span className="text-[12px] font-medium text-white/92">
                      发送到「{target.target_workflow_name}」
                    </span>
                    <span className="text-[14px] leading-none text-white/85">→</span>
                  </button>
                ))}
              </div>
              {isMultiOutput && selectedImages.length === 0 && (
                <div className="mt-1 text-center text-[11px] text-white/40">请至少勾选一张效果图</div>
              )}
            </div>
          )}
        </div>
      ))}
            {refineEditorModal}
      {recognitionModal}
      {imageEditModal}
      {toastEl}
    </>
  );
}
